import { useEffect, useState } from "react";
import TimeLineModal from "./order-modals/timeline-modal";
import { getRfqSummary } from "@/store/slices/rfqSlice";
import { useDispatch, useSelector } from "react-redux";
import { DocumentIcon } from "@heroicons/react/24/solid";
import { MapPinIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";

export function OrderSummary({ rfq_id, order_id, search, sortingOrder, activeTab = '' }) {

    const dispatch = useDispatch();

    const { t } = useTranslation('order');
    const [isTimelineModalOpen, setIsTimelineModalOpen] = useState(false);

    const { rfq_summary } = useSelector((state) => state.rfq);
    const { refund_requests, loading } = useSelector((state) => state.order);

    const amountAfterDiscount =
        Number(rfq_summary?.amount ?? 0) - Number(rfq_summary?.discount ?? 0);
    const vatAmount = Number(rfq_summary?.vat_add ?? 0);
    const effectiveVatPercent =
        rfq_summary?.is_vat_charge === "yes" &&
        Number.isFinite(amountAfterDiscount) &&
        amountAfterDiscount > 0 &&
        Number.isFinite(vatAmount) &&
        vatAmount > 0
            ? (vatAmount / amountAfterDiscount) * 100
            : null;
    
    const list = Array.isArray(refund_requests)
        ? refund_requests
        : refund_requests && typeof refund_requests === "object"
            ? [refund_requests]
            : [];

    useEffect(() => {
        dispatch(getRfqSummary(rfq_id));
    }, []);

    return (
        <>
            <div className="lg:col-span-1 overflow-hidden">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">{t('summary_title')}</h2>
                    {/* Clock Icon */}
                    <svg className="h-6 w-6 text-gray-800 cursor-pointer" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} onClick={() => {
                        setIsTimelineModalOpen(true);
                    }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <p className="text-xs text-gray-600 mb-4">{t('all_prices_in_sar')} ({rfq_summary?.currency_code})</p>

                {/* Total Items / Charges Block */}
                <div className="space-y-1 mb-6">
                    <p className="text-base text-gray-900 ml-2">{t('total_items_label')}</p>
                    <div className='flex justify-between items-center py-2 px-3 rounded-lg bg-gray-300'>
                        <span className="flex items-center space-x-2 text-sm font-normal">
                            <span className="text-gray-800">{t('total_items_label')}</span>
                        </span>
                        <span className="text-sm font-medium text-gray-800">
                            {rfq_summary?.totalItems}
                        </span>
                    </div>
                    {/* Customer Delivery Address */}
                    <div className="py-4 px-4 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-300 shadow-sm hover:shadow-md transition-all duration-200">
                        <div className="flex items-start justify-between gap-4">
                            
                            <div className="flex-1 min-w-0">
                                
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-1.5 h-5 rounded-full bg-black" />

                                    <p className="text-[11px] font-bold uppercase tracking-[1.5px] text-gray-600">
                                        {t('delivery_location_label')}
                                    </p>
                                </div>

                                <p
                                    className="text-sm font-semibold text-gray-900 leading-6 break-words"
                                    title={rfq_summary?.customer_delivery_address}
                                >
                                    {rfq_summary?.customer_delivery_address || (
                                        <span className="text-gray-400 italic font-normal">
                                            {t('no_address_provided')}
                                        </span>
                                    )}
                                </p>

                                {rfq_summary?.latitude && rfq_summary?.longitude && (
                                    <div className="mt-2 flex flex-wrap items-center gap-2">
                                        <span className="text-[10px] px-2 py-1 rounded-full bg-white border border-gray-300 text-gray-600 font-medium">
                                            {t('lat_label')}: {rfq_summary?.latitude}
                                        </span>

                                        <span className="text-[10px] px-2 py-1 rounded-full bg-white border border-gray-300 text-gray-600 font-medium">
                                            {t('lng_label')}: {rfq_summary?.longitude}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {rfq_summary?.latitude && rfq_summary?.longitude && (
                                <div className="shrink-0 flex flex-col items-center gap-1">
                                    <a
                                        href={`https://www.google.com/maps?q=${rfq_summary.latitude},${rfq_summary.longitude}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={t('open_in_google_maps')}
                                        className="group flex items-center justify-center h-11 w-11 rounded-2xl bg-black text-white hover:bg-gray-800 hover:scale-105 active:scale-95 shadow-sm hover:shadow-lg transition-all duration-200"
                                    >
                                        <MapPinIcon className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                                    </a>

                                    <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
                                        {t('map_label')}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className='flex justify-between items-center py-2 px-3 rounded-lg bg-gray-300'>
                        <span className="flex items-center space-x-2 text-sm font-normal">
                            <input
                                type="checkbox"
                                checked={rfq_summary?.delivery_charge > 0}
                                className="form-checkbox h-4 w-4 bg-gray-900 text-gray-900 border-gray-900 accent-[#000000] rounded-sm focus:ring-0 cursor-pointer"
                                readOnly
                            />
                            <span className="text-gray-800">{t('delivery_charge_label')}</span>
                        </span>
                        <span className="text-sm font-medium text-gray-800">
                            {rfq_summary?.delivery_charge ?? 0}
                        </span>
                    </div>
                    <div className='flex justify-between items-center py-2 px-3 rounded-lg bg-gray-300'>
                        <span className="flex items-center space-x-2 text-sm font-normal">
                            <input
                                type="checkbox"
                                checked={rfq_summary?.installation_no_count == 0}
                                className="form-checkbox h-4 w-4 bg-gray-900 text-gray-900 border-gray-900 accent-[#000000] rounded-sm focus:ring-0 cursor-pointer"
                                // onClick={(e) => isInstallationCharge(e)}
                                readOnly
                            />
                            <span className="text-gray-800">{t('installation_charge_label')}</span>
                        </span>
                        <span className="text-sm font-medium text-gray-800">
                            {rfq_summary?.installation_add}
                        </span>
                    </div>
                </div>

                {/* Sub Total Block */}
                <div className="space-y-1">
                    <h3 className="text-lg font-semibold text-gray-900">{t('sub_total_title')}</h3>
                    <div className='flex justify-between items-center py-2 px-3 rounded-lg bg-gray-300'>
                        <span className="flex items-center space-x-2 text-sm font-normal">
                            <span className="text-gray-800">{t('amount_label')}</span>
                        </span>
                        <span className="text-sm font-medium text-gray-800">
                            {rfq_summary?.amount}
                        </span>
                    </div>
                     <div className='flex justify-between items-center py-2 px-3 rounded-lg bg-gray-300'>
                        <span className="flex items-center space-x-2 text-sm font-normal">
                            <span className="text-gray-800">{t('discount_label')}</span>
                        </span>
                        <span className="text-sm font-medium text-gray-800">
                            {rfq_summary?.discount}
                        </span>
                    </div>
                     <div className='flex justify-between items-center py-2 px-3 rounded-lg bg-gray-300'>
                        <span className="flex items-center space-x-2 text-sm font-normal">
                            <span className="text-gray-800">{t('amount_after_discount_label')}</span>
                        </span>
                        <span className="text-sm font-medium text-gray-800">
                            {(rfq_summary?.amount - rfq_summary?.discount)}
                        </span>
                    </div>
                    {/* <div className='flex justify-between items-center py-2 px-3 rounded-lg bg-gray-300'>
                        <span className="flex items-center space-x-2 text-sm font-normal">
                            <span className="text-gray-800">{t('amount_after_discount_label')}</span>
                        </span>
                        <span className="text-sm font-medium text-gray-800">
                            {((rfq_summary?.amount - rfq_summary?.discount) + rfq_summary?.installation_add)}
                        </span>
                    </div> */}
                    <div className='flex flex-col gap-1 py-2 px-3 rounded-lg bg-gray-300'>
                        <div className="flex justify-between items-center w-full">
                            <span className="flex items-center space-x-2 text-sm font-normal">
                                <input
                                    type="checkbox"
                                    checked={rfq_summary?.is_vat_charge === 'yes'}
                                    className="form-checkbox h-4 w-4 bg-gray-900 text-gray-900 border-gray-900 accent-[#000000] rounded-sm focus:ring-0 cursor-pointer"
                                    readOnly
                                />
                                <span className="text-gray-800">{t('total_vat_sar_label')} ({rfq_summary?.currency_code})</span>
                            </span>
                            <span className="text-sm font-medium text-gray-800">
                                {rfq_summary?.is_vat_charge === "yes"
                                    ? vatAmount
                                    : "0.00"}
                            </span>
                        </div>
                        {rfq_summary?.is_vat_charge === "yes" && effectiveVatPercent != null && (
                            <p className="text-[11px] text-gray-600 pl-7 leading-snug">
                                {t('vat_percentage_of_amount', { percentage: effectiveVatPercent.toFixed(2), amount: amountAfterDiscount , currency_code:rfq_summary?.currency_code})}
                            </p>
                        )}
                        {rfq_summary?.is_vat_charge !== "yes" && (
                            <p className="text-[11px] text-gray-500 pl-7">
                                {t('vat_not_applied')}
                            </p>
                        )}
                    </div>
                </div>

                {/* Payable Amount Footer */}
                <div className="mt-3 pt-4 border-t border-gray-100 text-right">
                    <p className="text-base text-gray-700">{t('payable_amount_sar_label')} ({rfq_summary?.currency_code})</p>
                    <p className="text-4xl font-extrabold text-gray-900 mt-1">{rfq_summary?.payableAmount}</p>
                </div>
                {isTimelineModalOpen && (
                    <TimeLineModal
                        isOpen={isTimelineModalOpen}
                        onClose={() => setIsTimelineModalOpen(false)}
                        orderId={order_id}
                    />
                )}
            </div>

          
            {refund_requests && list.length !== 0 && activeTab == 'refund' && (
                <div className="mt-6 space-y-3">
                    {list?.map((item, index) => (
                        item?.refund_receipt_file && (
                            <div
                                key={index}
                                className="flex items-center justify-between 
                                        bg-gray-50 hover:bg-gray-100 
                                        border border-gray-200 
                                        rounded-2xl px-4 py-3 transition"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="h-9 w-9 flex items-center justify-center 
                                                    bg-green-100 text-green-600 
                                                    rounded-xl">
                                        <DocumentIcon className="h-5 w-5" />
                                    </div>

                                    <div>
                                        <p className="text-sm font-medium text-gray-800">
                                            {t('refund_slip_label', { index: index + 1 })}
                                        </p>
                                        <p className="text-xs text-gray-500 capitalize">
                                            {item?.status}
                                        </p>
                                    </div>
                                </div>

                                <button
                                    onClick={() => setIsModalOpen(item?.refund_receipt_file)}
                                    className="text-sm font-medium text-blue-600 hover:text-blue-800 transition"
                                >
                                    {t('view_button')}
                                </button>
                            </div>
                        )
                    ))}
                </div>
            )}
          </>

    )
}

export default OrderSummary;